import React, { useEffect, useState } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';

const resolveImg = (src) => {
  if (!src) return src;
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const DEFAULTS = {
  hero: { image: '${process.env.PUBLIC_URL}/images/page-banners/about-us.jpg_202608071713.jpeg' },
};

const P = `${process.env.PUBLIC_URL}/images`;

const FEATURES = [
  {
    img: `${P}/facilities/infrastructure.jpeg`,
    title: 'Modern Campus & Infrastructure',
    desc: 'Thoughtfully designed campus built to support academic excellence, safety and holistic growth.',
  },
  {
    img: `${P}/facilities/smart-classroom.jpg`,
    title: 'Digital Smart Classrooms',
    desc: 'Spacious, vibrant and tech-enabled classrooms equipped with interactive learning tools.',
  },
  {
    img: `${P}/facilities/library.JPG`,
    title: 'Library & Reading Spaces',
    desc: 'A rich collection of books and digital resources to fuel curiosity and imagination.',
  },
  {
    img: `${P}/facilities/musics.webp`,
    title: 'Music & Performing Arts',
    desc: 'Dedicated creative spaces for music, dance, instruments and stage confidence.',
  },
];

const CLUBS = [
  { icon: 'fa-landmark', label: 'MUN' },
  { icon: 'fa-users-viewfinder', label: 'TED' },
  { icon: 'fa-masks-theater', label: 'Performing Arts' },
  { icon: 'fa-theater-masks', label: 'Theatre' },
  { icon: 'fa-brain', label: 'Logical Reasoning' },
  { icon: 'fa-dumbbell', label: 'Physical Fitness' },
  { icon: 'fa-calculator', label: 'Maths' },
  { icon: 'fa-flask', label: 'Science' },
  { icon: 'fa-robot', label: 'AI & Robotics' },
];

const ACTIVITIES = [
  { icon: 'fa-futbol', label: 'Sports' },
  { icon: 'fa-heart-pulse', label: 'Taekwondo' },
  { icon: 'fa-music', label: 'Music' },
  { icon: 'fa-person-running', label: 'Dance' },
  { icon: 'fa-palette', label: 'Fine Arts' },
  { icon: 'fa-masks-theater', label: 'Theatre' },
];

const PATHWAYS = [
  { icon: 'fa-flask', title: 'Science', desc: 'Physics, Chemistry, Biology and Mathematics for future engineers, doctors and researchers.' },
  { icon: 'fa-briefcase', title: 'Commerce', desc: 'Accountancy, Business Studies and Economics to build tomorrow\'s entrepreneurs and analysts.' },
  { icon: 'fa-book', title: 'Humanities', desc: 'History, Political Science and Psychology for curious minds shaping society and culture.' },
];

const Hero = ({ src, alt }) => (
  <section className="page-hero">
    {src && <img src={src} alt={alt} className="page-hero-img" loading="eager" />}
  </section>
);

const SectionHeading = ({ kicker, title, center = false }) => (
  <Reveal>
    <div className={`section-title${center ? ' text-center' : ''}`}>
      <div className="kicker">{kicker}</div>
      <h2 className="h-section text-3xl md:text-4xl font-bold">{title}</h2>
      <div className={`divider-line${center ? ' mx-auto' : ''}`} />
    </div>
  </Reveal>
);

const AboutPage = () => {
  const [sectionData, setSectionData] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [activitiesList, setActivitiesList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionsRes, facilitiesRes, activitiesRes] = await Promise.allSettled([
          API.get('/home-sections'),
          API.get('/facilities/public'),
          API.get('/co-curricular-activities/public')
        ]);
        if (sectionsRes.status === 'fulfilled' && sectionsRes.value.data?.data) {
          setSectionData(sectionsRes.value.data.data);
        }
        if (facilitiesRes.status === 'fulfilled' && facilitiesRes.value.data?.data?.length) {
          setFacilities(facilitiesRes.value.data.data.map(f => ({
            img: resolveImg(f.image),
            title: f.title,
            desc: f.description ? f.description.replace(/<[^>]+>/g, '').trim() : ''
          })));
        }
        if (activitiesRes.status === 'fulfilled' && activitiesRes.value.data?.data?.length) {
          setActivitiesList(activitiesRes.value.data.data.map(a => ({
            icon: a.icon || 'fa-volleyball',
            label: a.title,
            img: resolveImg(a.image)
          })));
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const hero = sectionData.about_hero || DEFAULTS.hero;
  const heroImage = hero.image ? resolveImg(hero.image) : resolveImg(DEFAULTS.hero.image);
  const displayFeatures = (facilities.length > 0 ? facilities : FEATURES).slice(0, 4);

  return (
    <>
      <SEO
        slug="about-us"
        title="About St. Andrews World School, Indirapuram"
        description="About Indirapuram Campus — where learning inspires, talent flourishes and futures take shape at St. Andrews World School, a CISCE-affiliated school in Indirapuram."
        keywords="About SAWS Indirapuram, St Andrews World School, Indirapuram, ICSE School Indirapuram"
        ogImage={heroImage}
      />

      <Hero src={heroImage} alt="About St. Andrews World School, Indirapuram" />

      {/* ABOUT INDIRAPURAM CAMPUS */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-center">
            <Reveal className="order-2 lg:order-1">
              <img
                src={`${P}/about_us_best_school_indirapuram.jpg`}
                alt="St. Andrews World School, Indirapuram campus"
                className="w-full aspect-[4/3] object-cover rounded-2xl shadow-xl"
                loading="lazy"
              />
            </Reveal>
            <div className="order-1 lg:order-2">
              <Reveal>
                <div className="kicker">About Indirapuram Campus</div>
                <h2 className="h-section text-3xl md:text-4xl font-bold mt-2 text-left">Where Learning Inspires. Talent Flourishes. Futures Take Shape.</h2>
                <div className="divider-line mt-4" />
              </Reveal>
              <div className="mt-6 space-y-5 text-body text-[17px] leading-relaxed">
                <Reveal delayMs={80}>
                  <p>At St. Andrews World School, Indirapuram, education is designed to go beyond academics — to nurture curiosity, confidence, creativity, character and a lifelong love for learning. As a CISCE-affiliated, fully air-conditioned school, our campus brings together contemporary infrastructure, technology-enabled learning and a strong focus on the holistic development of every child.</p>
                </Reveal>
                <Reveal delayMs={160}>
                  <p>With personalised attention and a strong emphasis on child well-being, every learner is encouraged to discover their strengths and progress at their own pace.</p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACILITIES GRID */}
      <section className="section section-alt">
        <div className="container">
          <SectionHeading kicker="Campus & Facilities" title="A Modern Campus Built for Learning" center />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {displayFeatures.map((f, idx) => (
              <Reveal key={idx} delayMs={idx * 80}>
                <div className="card p-0 overflow-hidden h-full group hover:shadow-xl transition-all duration-500">
                  <div className="relative overflow-hidden">
                    <img 
                      src={f.img} 
                      alt={f.title} 
                      className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" 
                      loading="lazy"
                      onError={(e) => {
                        if (!e.target.dataset.tried) {
                          e.target.dataset.tried = 1;
                          e.target.src = f.img.includes('/admin/public/uploads') 
                            ? f.img.replace('/admin/public/uploads', '/admin/uploads') 
                            : f.img;
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-primary font-bold text-lg">{f.title}</h3>
                    {f.desc && <p className="text-body text-[15px] leading-relaxed mt-2">{f.desc}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING BEYOND THE CLASSROOM */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[1000px] mx-auto">
            <SectionHeading kicker="Beyond Academics" title="Learning Beyond the Classroom" center />
            <Reveal>
              <p className="mt-6 text-center text-body text-[17px] leading-relaxed max-w-[820px] mx-auto">A wide range of opportunities allows students to explore and excel in areas beyond academics — indoor and outdoor sports, Taekwondo, music, dance, fine arts, theatre and performing arts.</p>
            </Reveal>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {(activitiesList.length > 0 ? activitiesList : ACTIVITIES).map((a, idx) => (
                <Reveal key={idx} delayMs={idx * 50}>
                  <div className="card p-5 h-full text-center flex flex-col items-center justify-center group hover:border-secondary/40 transition-all">
                    <div className="w-12 h-12 mx-auto rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                      <i className={`fas ${a.icon}`} aria-hidden />
                    </div>
                    <p className="text-primary font-semibold text-sm mt-3">{a.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delayMs={120}>
              <div className="mt-12">
                <p className="text-center text-body text-[17px] font-semibold">Our vibrant weekly club programme includes</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {CLUBS.map((c, i) => (
                    <Reveal key={c.label} delayMs={i * 40}>
                      <div className="card p-5 h-full text-center flex flex-col items-center justify-center gap-3 bg-primary text-white border-primary hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 group">
                        <div className="w-12 h-12 rounded-full bg-white/10 text-[#F3D27E] flex items-center justify-center transition-all duration-300">
                          <i className={`fas ${c.icon}`} aria-hidden />
                        </div>
                        <p className="text-[13px] font-semibold leading-snug text-center">{c.label}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delayMs={180}>
              <p className="mt-8 text-center text-body text-[17px] leading-relaxed max-w-[820px] mx-auto">These experiences develop leadership, communication, collaboration, critical thinking, creativity and problem-solving skills, preparing students for a rapidly evolving world.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PATHWAYS FOR EVERY ASPIRATION */}
      <section className="section section-alt">
        <div className="container">
          <SectionHeading kicker="Senior Secondary" title="Pathways for Every Aspiration" center />
          <Reveal>
            <p className="mt-6 text-center text-body text-[17px] leading-relaxed max-w-[820px] mx-auto">At the Senior Secondary level, students can pursue Science, Commerce or Humanities, with the freedom to choose a pathway aligned with their interests and future aspirations.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PATHWAYS.map((pth, idx) => (
              <Reveal key={idx} delayMs={idx * 90}>
                <div className="card p-7 md:p-8 h-full group hover:shadow-lg transition-all relative overflow-hidden">
                  <div className="absolute -right-10 -top-10 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                    <i className={`fas ${pth.icon}`} aria-hidden />
                  </div>
                  <h3 className="text-primary text-xl font-bold mt-5">{pth.title}</h3>
                  <p className="text-body text-[15px] leading-relaxed mt-3">{pth.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;